import { useLayoutEffect } from "react"
import { useBooking } from "@/context/BookingContext"
import { usePortalContainer } from "./PortalContainer"

// Applies API-provided event styling — the color scheme (theme) and primary
// brand color — once event data loads, locking the rendered content to whatever
// the event specifies. In a widget it targets the shadow-root wrapper; for a
// creative the studio supplies the cell as the portal container; in the SPA
// there's no portal container so it falls back to the document root. Because
// each consumer has its own target element, a widget/creative stays on its
// event's theme regardless of the surrounding (showcase) theme — CSS tokens
// resolve to the nearest theme-class ancestor (see the `.light`/`.dark` blocks
// in index.css).
export function useBrandingSync() {
  const { state } = useBooking()
  const event = state.event
  const primaryColor = event?.primaryColor
  const secondaryColor = event?.secondaryColor
  // Events without an explicit scheme default to dark (the product default).
  const theme = event?.theme ?? "dark"
  const container = usePortalContainer()

  useLayoutEffect(() => {
    if (!event) return
    const el = container ?? document.documentElement

    if (!import.meta.env.DEV) {
      el.classList.remove("light", "dark")
      el.classList.add(theme)
    }

    if (primaryColor) {
      el.style.setProperty("--primary", primaryColor)
      el.style.setProperty("--ring", primaryColor)
      el.style.setProperty("--sidebar-primary", primaryColor)
      el.style.setProperty("--sidebar-ring", primaryColor)
    }

    // The gradients end on the second brand colour when the event names one,
    // and the hue rotation that used to stand in for it comes off — that shift
    // is why a partner's chosen colour came out as something they never picked.
    // Without a second colour both are left unset, so the gradients keep their
    // existing fallbacks and every event already out there looks unchanged.
    if (secondaryColor) {
      el.style.setProperty("--brand-secondary", secondaryColor)
      el.style.setProperty("--brand-hue-rotate", "0deg")
    } else {
      el.style.removeProperty("--brand-secondary")
      el.style.removeProperty("--brand-hue-rotate")
    }
  }, [event, theme, primaryColor, secondaryColor, container])
}
